// ============================================
// CarMarkerIcon — Professional Car SVG for Google Maps
// Replaces generic arrows with Yango/InDrive-quality icons
// ============================================

// ── Icon cache to prevent re-creating identical SVG data URIs ──
const iconCache = new Map();

/**
 * Generates a Google Maps marker icon config with a professional top-down car SVG.
 * Uses aggressive caching to prevent icon flickering during real-time updates.
 * 
 * @param {'active'|'idle'|'selected'} state - Visual state of the marker
 * @param {number} heading - Rotation angle in degrees (0 = North)
 * @param {number} scale - Scale multiplier (default 0.6)
 * @param {number} index - Index for matching avatar color
 * @returns {object} Google Maps Icon configuration object
 */
export const getCarMarkerIcon = (state = 'active', heading = 0, scale = 0.6, index = 0) => {
    // Round heading to nearest 5° to keep cache small but smooth enough
    const roundedHeading = Math.round(heading / 5) * 5;
    const cacheKey = `${state}_${roundedHeading}_${scale}_${index}`;
    
    if (iconCache.has(cacheKey)) {
        return iconCache.get(cacheKey);
    }

    // Palette matching avatar gradients (emerald, sky/blue, amber/orange, rose/pink, violet/purple)
    const baseColors = [
        { body: '#10b981', stroke: '#065f46', window: '#d1fae5', glow: '#10b981' }, // Emerald
        { body: '#0ea5e9', stroke: '#0369a1', window: '#e0f2fe', glow: '#0ea5e9' }, // Sky
        { body: '#f59e0b', stroke: '#92400e', window: '#fef3c7', glow: '#f59e0b' }, // Amber
        { body: '#fb7185', stroke: '#be123c', window: '#ffe4e6', glow: '#fb7185' }, // Rose
        { body: '#8b5cf6', stroke: '#4c1d95', window: '#ede9fe', glow: '#8b5cf6' }, // Violet
    ];

    const c = state === 'idle' 
        ? { body: '#94a3b8', stroke: '#475569', window: '#e2e8f0', glow: '#94a3b8' } // Slate for idle
        : baseColors[index % baseColors.length];

    // Premium top-down car SVG — vivid headlight beams for clear direction indication
    const selectedRing = state === 'selected' ? `
        <circle cx="32" cy="32" r="30" fill="none" stroke="${c.glow}" stroke-width="2" opacity="0.4"/>
        <circle cx="32" cy="32" r="28" fill="${c.glow}" opacity="0.08"/>
    ` : '';

    // Use a larger viewBox (64x64) to accommodate headlight beam effects outside car body
    const svgStr = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64">
            <defs>
                <filter id="shadow" x="-30%" y="-30%" width="160%" height="160%">
                    <feDropShadow dx="0" dy="1.5" stdDeviation="2.5" flood-opacity="0.35"/>
                </filter>
                <radialGradient id="hlGlowL" cx="26" cy="10" r="10" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stop-color="#fef08a" stop-opacity="0.95"/>
                    <stop offset="40%" stop-color="#fbbf24" stop-opacity="0.5"/>
                    <stop offset="100%" stop-color="#fbbf24" stop-opacity="0"/>
                </radialGradient>
                <radialGradient id="hlGlowR" cx="38" cy="10" r="10" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stop-color="#fef08a" stop-opacity="0.95"/>
                    <stop offset="40%" stop-color="#fbbf24" stop-opacity="0.5"/>
                    <stop offset="100%" stop-color="#fbbf24" stop-opacity="0"/>
                </radialGradient>
                <linearGradient id="beamL" x1="26" y1="14" x2="26" y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stop-color="#fbbf24" stop-opacity="0.6"/>
                    <stop offset="100%" stop-color="#fef9c3" stop-opacity="0"/>
                </linearGradient>
                <linearGradient id="beamR" x1="38" y1="14" x2="38" y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stop-color="#fbbf24" stop-opacity="0.6"/>
                    <stop offset="100%" stop-color="#fef9c3" stop-opacity="0"/>
                </linearGradient>
            </defs>
            ${selectedRing}
            <g transform="rotate(${roundedHeading}, 32, 32)" filter="url(#shadow)">
                <!-- ★ HEADLIGHT BEAM CONES — bright triangular glow projecting forward -->
                <polygon points="24,14 20,0 28,0" fill="url(#beamL)"/>
                <polygon points="40,14 36,0 44,0" fill="url(#beamR)"/>
                <!-- ★ HEADLIGHT GLOW HALOS — large radial glow around each headlight -->
                <circle cx="26" cy="10" r="9" fill="url(#hlGlowL)"/>
                <circle cx="38" cy="10" r="9" fill="url(#hlGlowR)"/>
                <!-- Car body -->
                <rect x="22" y="14" width="20" height="36" rx="8" ry="8" 
                      fill="${c.body}" stroke="${c.stroke}" stroke-width="1.8"/>
                <!-- ★ FRONT BUMPER ACCENT — bright white strip to mark front -->
                <rect x="24" y="14" width="16" height="2.5" rx="1.2" fill="#ffffff" opacity="0.7"/>
                <!-- Windshield (front) -->
                <rect x="25" y="18" width="14" height="8" rx="3.5" ry="3.5" 
                      fill="${c.window}" opacity="0.85"/>
                <!-- Rear window -->
                <rect x="25" y="38" width="14" height="7" rx="3.5" ry="3.5" 
                      fill="${c.window}" opacity="0.5"/>
                <!-- Left wheels -->
                <rect x="18" y="21" width="6" height="7" rx="2.5" fill="${c.stroke}" opacity="0.75"/>
                <rect x="18" y="36" width="6" height="7" rx="2.5" fill="${c.stroke}" opacity="0.75"/>
                <!-- Right wheels -->
                <rect x="40" y="21" width="6" height="7" rx="2.5" fill="${c.stroke}" opacity="0.75"/>
                <rect x="40" y="36" width="6" height="7" rx="2.5" fill="${c.stroke}" opacity="0.75"/>
                <!-- Direction arrow on roof -->
                <path d="M32 24 L36 32 L32 30 L28 32 Z" fill="${c.window}" opacity="0.9"/>
                <!-- ★ HEADLIGHTS — bright, large, unmistakable -->
                <circle cx="26" cy="15" r="3" fill="#fef08a" opacity="1"/>
                <circle cx="26" cy="15" r="1.8" fill="#ffffff" opacity="0.95"/>
                <circle cx="38" cy="15" r="3" fill="#fef08a" opacity="1"/>
                <circle cx="38" cy="15" r="1.8" fill="#ffffff" opacity="0.95"/>
                <!-- Taillights — small red, clearly different from front -->
                <rect x="24" y="48" width="5" height="2" rx="1" fill="#ef4444" opacity="0.9"/>
                <rect x="35" y="48" width="5" height="2" rx="1" fill="#ef4444" opacity="0.9"/>
            </g>
        </svg>
    `.replace(/\n\s+/g, '');

    const encoded = encodeURIComponent(svgStr);

    const icon = {
        url: `data:image/svg+xml,${encoded}`,
        scaledSize: window.google?.maps ? new window.google.maps.Size(64 * scale, 64 * scale) : undefined,
        anchor: window.google?.maps ? new window.google.maps.Point(32 * scale, 32 * scale) : undefined,
    };

    iconCache.set(cacheKey, icon);
    return icon;
};

/**
 * Generates a simple directional arrow icon (fallback for when car is too small)
 */
export const getArrowMarkerIcon = (state = 'active', heading = 0) => {
    const colors = {
        active: '#10b981',
        idle: '#94a3b8',
        selected: '#3b82f6',
        onMission: '#f59e0b',
    };

    if (!window.google?.maps) return null;

    return {
        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 7,
        fillColor: colors[state] || colors.active,
        fillOpacity: 1,
        strokeWeight: 2.5,
        strokeColor: '#ffffff',
        rotation: heading,
        anchor: new window.google.maps.Point(0, 2.5),
    };
};

export default getCarMarkerIcon;
